import { FC, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { ImageState } from './ImageCompareDialog';

interface ImageCompareSliderProps {
  leftImage: ImageState;
  rightImage: ImageState;
  className?: string;
}

export const ImageCompareSlider: FC<ImageCompareSliderProps> = ({
  leftImage,
  rightImage,
  className,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const position = ((clientX - left) / width) * 100;
    setPosition(Math.min(Math.max(0, position), 100));
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.clientX);
  };

  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', () => setIsDragging(false));
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', () => setIsDragging(false));

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', () => setIsDragging(false));
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', () => setIsDragging(false));
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative aspect-square h-full w-full select-none overflow-hidden rounded-md',
        isMobile && 'order-2',
        className,
      )}
    >
      {/* Right image */}
      <img
        src={rightImage.webp_url || rightImage.file_link || ''}
        alt={rightImage.description}
        className="absolute inset-0 h-full w-full object-contain"
        onError={(e) => {
          const target = e.currentTarget as HTMLImageElement;
          if (rightImage.webp_url && target.src !== (rightImage.file_link || '')) {
            target.src = rightImage.file_link || '';
          }
        }}
      />

      {/* Left image with clip path */}
      <img
        src={leftImage.webp_url || leftImage.file_link || ''}
        alt={leftImage.description}
        className="absolute inset-0 h-full w-full bg-white object-contain"
        style={{
          clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)`,
        }}
        onError={(e) => {
          const target = e.currentTarget as HTMLImageElement;
          if (leftImage.webp_url && target.src !== (leftImage.file_link || '')) {
            target.src = leftImage.file_link || '';
          }
        }}
      />

      {/* Slider handle */}
      <div
        className="absolute bottom-0 top-0 z-10 w-0.5 cursor-ew-resize border-2 border-dark-red bg-transparent"
        style={{ left: `${position}%` }}
        onMouseDown={() => setIsDragging(true)}
        onTouchStart={() => setIsDragging(true)}
      >
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-transparent p-2">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16" cy="16" r="15.5" fill="#E0E9F0" stroke="#BF3636" />
            <path
              d="M19 25.7964L27 16.3981L19 6.99982"
              stroke="#002C41"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 25.7964L5 16.3981L13 6.99982"
              stroke="#002C41"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};
